import { useState, useEffect } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from "react-redux";

// Local imports
import axiosInstance from "../../../../API/axios";
import { setSnackbar } from "../../../../Redux/snackBarSlice";
import { setData } from "../../../../Redux/requestDataSlice";
import { set } from 'date-fns';
import {
  PLAN_TYPES,
  normalizePlanType,
  isExemption,
  validateExemptionFields,
  toImpactForApi,
  EMPTY_EXEMPTION_FIELDS,
} from '@Utils/planType';

// ===================== CONSTANTS - URLs & STRINGS =====================
// API endpoints
const API_ENDPOINTS = {
  GET_SUBMISSION: '/exit-request-response/my-submissions/',
  CREATE_EXIT_PLAN: '/exit-plans',
  GET_BY_RESPONSE_ID: '/exit-request-response/submission-by-response-id/',
  UPDATE_EXIT_PLAN: '/exit-plans/',
  DELETE_EXIT_PLAN: '/exit-plans/',
  SUBMIT_EXIT_PLAN: '/exit-plans/' // For submit: /exit-plans/{exitPlanId}/submit
};

// Success messages
const SUCCESS_MESSAGES = {
  PLAN_CREATED: 'Exit plan created successfully!',
  PLAN_UPDATED: 'Exit plan updated successfully!',
  PLAN_DELETED: 'Exit plan deleted successfully!'
};

// Error messages
const ERROR_MESSAGES = {
  NO_RESPONSE: 'No Impact Assessment Found.',
  FETCH_FAILED: 'Failed to fetch exit request details.',
  CREATE_FAILED: 'Error creating exit plan.',
  UPDATE_FAILED: 'Error updating exit plan.',
  DELETE_FAILED: 'Failed to delete exit plan.'
};

// Form validation messages
const VALIDATION_MESSAGES = {
  EXIT_DATE_REQUIRED: 'Stop production date is required',
  REPLACEMENT_PLAN_REQUIRED: 'Replacement plan is required',
  END_PRODUCTION_DATE_REQUIRED: 'End of production date is required',
  END_IMPORTATION_DATE_REQUIRED: 'End of importation date is required',
  LIQUIDATION_PLAN_REQUIRED: 'Liquidation plan is required',
  SUPPORT_TEAM_REQUIRED: 'Please select at least one support team',
  MARKETING_MEMBER_REQUIRED: 'Please select a marketing team member',
  SUPPLY_CHAIN_MEMBER_REQUIRED: 'Please select a supply chain team member'
};

// Restriction types
const RESTRICTION_TYPES = {
  BANNED: 'BANNED',
  RESTRICTED: 'RESTRICTED',
  LABELING_REQUIREMENTS: 'LABELING_REQUIREMENTS',
  RESTRICTIONS_AND_LABELING: 'RESTRICTIONS_AND_LABELING',
  RESTRICTED_AND_SDS: 'RESTRICTED_AND_SDS'
};

// ===================== CUSTOM HOOK =====================
export const useExitPlanData = (isEditing, { initialPlanType = PLAN_TYPES.EXIT_PLAN } = {}) => {
  // ===== Hooks and Redux =====
  const { reqId, categoryAssignmentId  } = useParams();
  const { responseId  } = useParams();
  const user = useSelector((state) => state.user);
  const userId = useSelector((state) => state.user.userId);
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const requestDataFromRedux = useSelector(state => state.requestData.requestData);

  // ===== State Management =====
  // Request data state
  const [requestData, setRequestData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [formErrors, setFormErrors] = useState({});

  // Form field states
  const [exitDate, setExitDate] = useState(null);
  const [endOfProductionDate, setEndOfProductionDate] = useState(null);
  const [endOfImportationDate, setEndOfImportationDate] = useState(null);
  const [replacementPlan, setReplacementPlan] = useState('');
  const [liquidationPlan, setLiquidationPlan] = useState('');
  const [additionalComments, setAdditionalComments] = useState('');

  // Plan type + exemption-only fields (see @Utils/planType)
  const [planType, setPlanType] = useState(normalizePlanType(initialPlanType));
  const [toImpactEuros, setToImpactEuros] = useState(EMPTY_EXEMPTION_FIELDS.toImpactEuros);
  const [exceptionStartDate, setExceptionStartDate] = useState(EMPTY_EXEMPTION_FIELDS.exceptionStartDate);
  const [exceptionEndDate, setExceptionEndDate] = useState(EMPTY_EXEMPTION_FIELDS.exceptionEndDate);
  const [justification, setJustification] = useState(EMPTY_EXEMPTION_FIELDS.justification);
  const [safetyAssessmentReference, setSafetyAssessmentReference] = useState(EMPTY_EXEMPTION_FIELDS.safetyAssessmentReference);
  const [productTypes, setProductTypes] = useState(EMPTY_EXEMPTION_FIELDS.productTypes);
  const [brands, setBrands] = useState(EMPTY_EXEMPTION_FIELDS.brands);

  // Support team states
  const [needsSupport, setNeedsSupport] = useState(false);
  const [marketingSupport, setMarketingSupport] = useState(false);
  const [supplyChainSupport, setSupplyChainSupport] = useState(false);
  const [selectedMarketingMember, setSelectedMarketingMember] = useState(null);
  const [selectedSupplyChainMember, setSelectedSupplyChainMember] = useState(null);


  // Submit button loading state
  const [submitLoading, setSubmitLoading] = useState(false);


  const [readOnly, setReadOnly] = useState(false);

  // ===== User Permission Checks =====
  // Check if current user is the one who submitted the data
  const isSubmitter = !!(
    requestData &&
    requestData.submittedBy &&
    requestData.submittedBy.id === userId
  );

  // Check if user can edit (is submitter and not in read-only mode)
  const canEdit = isSubmitter;

  // ===== Effects =====
  /**
   * Fetches request data either from Redux state or API
   */
 useEffect(() => {
    const fetchRequestData = async () => {
      // Check if data is available in Redux state
      if (requestDataFromRedux!= null && requestDataFromRedux?.exitRequest?.id == reqId && requestDataFromRedux?.categoryAssignmentId == categoryAssignmentId ) {
        setRequestData(requestDataFromRedux);
        console.log("Using Redux state for request data:", requestDataFromRedux);
        setLoading(false);
        return;
      }

      setLoading(true);
      try {
        let response;
        if (responseId) {
          setReadOnly(true);
          response = await axiosInstance.get(`${API_ENDPOINTS.GET_BY_RESPONSE_ID}${responseId}`);
          console.log("Aaaa")
          console.log(response.data);
          console.log(user);
          response?.data.exitRequest.targetedMembers.map((member) => {
            if (user.email == member.email) {
              console.log("Member is the targeted member");
              setReadOnly(false);
            }
          })
        } else {
          setReadOnly(false)
          response = await axiosInstance.get(`${API_ENDPOINTS.GET_SUBMISSION}${reqId}/${categoryAssignmentId}`);
        }
        if (response.data.id === null) {
          dispatch(setSnackbar({
            open: true,
            message: ERROR_MESSAGES.NO_RESPONSE,
            error: true
          }));
          setLoading(false);
          return;
        }
         console.log("Response data:", response.data);
        setRequestData(response.data);
        dispatch(setData(response.data));
      } catch (error) {
        dispatch(setSnackbar({
          open: true,
          message: ERROR_MESSAGES.FETCH_FAILED,
          error: true
        }));
      } finally {
        setLoading(false);
      }
    };

    // Only fetch if requestData is not already set for this reqId/categoryAssignmentId
    if (
      !requestData ||
      requestData?.exitRequest?.id != reqId ||
      requestData?.categoryAssignmentId != categoryAssignmentId
    ) {
      fetchRequestData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reqId, categoryAssignmentId, responseId, isEditing, dispatch]);

  // ===== Validation Functions =====
  /**
   * Validates the form based on restriction type and selected options
   * @returns {boolean} True if form is valid, false otherwise
   */
  const validateForm = () => {
    const errors = {};

    // Validate based on restriction type
    // if (requestData?.exitRequest?.restrictionType === RESTRICTION_TYPES.BANNED) {
    //   if (!exitDate) errors.exitDate = VALIDATION_MESSAGES.EXIT_DATE_REQUIRED;
    //   if (!replacementPlan) errors.replacementPlan = VALIDATION_MESSAGES.REPLACEMENT_PLAN_REQUIRED;
    // } else if (
    if (
      requestData?.exitRequest?.restrictionType === RESTRICTION_TYPES.BANNED ||
      requestData?.exitRequest?.restrictionType === RESTRICTION_TYPES.RESTRICTED ||
      requestData?.exitRequest?.restrictionType === RESTRICTION_TYPES.LABELING_REQUIREMENTS ||
      requestData?.exitRequest?.restrictionType === RESTRICTION_TYPES.RESTRICTIONS_AND_LABELING ||
      requestData?.exitRequest?.restrictionType === RESTRICTION_TYPES.RESTRICTED_AND_SDS
    ) {
      if (!endOfProductionDate && !endOfImportationDate) {
        errors.endOfProductionDate = 'At least one date (End of Production or End of Importation) is required';
        errors.endOfImportationDate = 'At least one date (End of Production or End of Importation) is required';
      }
      if (!liquidationPlan) errors.liquidationPlan = VALIDATION_MESSAGES.LIQUIDATION_PLAN_REQUIRED;
    }

    // Exemption-only fields
    if (isExemption(planType)) {
      Object.assign(errors, validateExemptionFields({ toImpactEuros, exceptionStartDate, exceptionEndDate, justification }));
    }

    // Validate support team selection
    if (needsSupport) {
      if (!marketingSupport && !supplyChainSupport) {
        errors.supportTeams = VALIDATION_MESSAGES.SUPPORT_TEAM_REQUIRED;
      }
      if (marketingSupport && !selectedMarketingMember) {
        errors.supportTeams = VALIDATION_MESSAGES.MARKETING_MEMBER_REQUIRED;
      }
      if (supplyChainSupport && !selectedSupplyChainMember) {
        errors.supportTeams = VALIDATION_MESSAGES.SUPPLY_CHAIN_MEMBER_REQUIRED;
      }
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // ===== Handler Functions =====
  /**
   * Handles form submission (placeholder for now)
   */
  const handleSubmit = () => {
    if (validateForm()) {
      // Form is valid - actual submission is handled by createExitPlan
      return true;
    }
    return false;
  };

  // ===== API Functions =====
  /**
   * Creates or updates an exit plan
   * @param {Object} planData - Exit plan data
   * @returns {Promise<Object>} Created/updated plan data
   */
  const createExitPlan = async ({
                                  planId,
                                  exitRequestResponseId,
                                  additionalComments,
                                  exitDate,
                                  replacementPlan,
                                  endOfProductionDate,
                                  endOfImportationDate,
                                  liquidationPlan,
                                  marketingSupport,
                                  supplyChainSupport,
                                  supportUserIds,
                                  products,
                                  updating = false,
                                  planType = PLAN_TYPES.EXIT_PLAN,
                                  toImpactEuros: toImpactValue = null,
                                  exceptionStartDate: exceptionStart = null,
                                  exceptionEndDate: exceptionEnd = null,
                                  justification: justificationText = '',
                                  safetyAssessmentReference: safetyRef = '',
                                  productTypes: productTypesValue = '',
                                  brands: brandsValue = '',
                                }) => {
    setLoading(true);

    const label = isExemption(planType) ? 'Exemption request' : 'Exit plan';
    const successMessage = `${label} ${updating ? 'updated' : 'created'} successfully!`;
    const errorMessage = `Error ${updating ? 'updating' : 'creating'} ${label.toLowerCase()}.`;
    const url = updating ? `${API_ENDPOINTS.UPDATE_EXIT_PLAN}${planId}` : API_ENDPOINTS.CREATE_EXIT_PLAN;

    try {
      // Prepare payload
      const payload = {
        exitRequestResponseId,
        additionalComments,
        exitDate,
        replacementPlan,
        endOfProductionDate,
        endOfImportationDate,
        liquidationPlan,
        marketingSupport,
        supplyChainSupport,
        supportUserIds,
        products,
        planType: normalizePlanType(planType),
        toImpactEuros: isExemption(planType) ? toImpactForApi(toImpactValue) : null,
        exceptionStartDate: isExemption(planType) ? exceptionStart : null,
        exceptionEndDate: isExemption(planType) ? exceptionEnd : null,
        justification: isExemption(planType) ? justificationText : null,
        safetyAssessmentReference: isExemption(planType) ? safetyRef : null,
        productTypes: isExemption(planType) ? productTypesValue : null,
        brands: isExemption(planType) ? brandsValue : null,
      };
      console.log(payload);
      // Make API call
      const response = updating
          ? await axiosInstance.put(url, payload)
          : await axiosInstance.post(url, payload);

      // Show success message
      dispatch(setSnackbar({
        open: true,
        message: successMessage,
        error: false
      }));

      // Update Redux state
      updateReduxExitPlans(response.data, updating);

      // Update local requestData state so new/updated plan and products are available immediately
      setRequestData(prev => {
        if (!prev) return prev;
        let updatedExitPlans;
        if (updating) {
          updatedExitPlans = (prev.exitPlans || []).map(plan =>
            plan.id === response.data.id ? response.data : plan
          );
        } else {
          updatedExitPlans = [...(prev.exitPlans || []), response.data];
        }

        // Optionally update products in requestData if backend returns updated products
        let updatedProducts = prev.products;
        if (response.data.products) {
          // If backend returns the full updated products array for the plan, update them
          updatedProducts = prev.products.map(prod => {
            const updatedProd = response.data.products.find(p => p.productId === prod.productId);
            return updatedProd ? { ...prod, ...updatedProd } : prod;
          });
        }

        return {
          ...prev,
          exitPlans: updatedExitPlans,
          products: updatedProducts
        };
      });

      // Navigate with updated plan ID
      navigate(location.pathname, { state: { planId: response.data.id } });

      return response.data;
    } catch (error) {
      dispatch(setSnackbar({
        open: true,
        message: errorMessage,
        error: true
      }));
      throw error;
    } finally {
      setLoading(false);
    }
  };

  /**
   * Deletes an exit plan
   * @param {number} planId - ID of the plan to delete
   * @returns {Promise<void>}
   */
  const deleteExitPlan = async (planId) => {
    setLoading(true);

    try {
      await axiosInstance.delete(`${API_ENDPOINTS.DELETE_EXIT_PLAN}${planId}`);

      // Show success message
      dispatch(setSnackbar({
        open: true,
        message: SUCCESS_MESSAGES.PLAN_DELETED,
        error: false
      }));

      // Update Redux state to remove deleted plan and return products to all products list
      dispatch((dispatch, getState) => {
        const current = getState().requestData.requestData;
        const deletedPlan = (current.exitPlans || []).find(plan => plan.id === planId);
        const updatedExitPlans = (current.exitPlans || []).filter(plan => plan.id !== planId);

        // Return products to all products list
        let updatedProducts = current.products || [];
        if (deletedPlan && Array.isArray(deletedPlan.products)) {
          // Remove any product flags that marked them as planned
          updatedProducts = updatedProducts.map(prod => {
            if (deletedPlan.products.some(p => p.productId === prod.productId)) {
              // Optionally reset any plan-specific fields here
              return { ...prod };
            }
            return prod;
          });
        }

        dispatch(setData({
          ...current,
          exitPlans: updatedExitPlans,
          products: updatedProducts
        }));
      });

      // Update local requestData state to remove deleted plan and return products
      setRequestData(prev => {
        if (!prev) return prev;
        const deletedPlan = (prev.exitPlans || []).find(plan => plan.id === planId);
        const updatedExitPlans = (prev.exitPlans || []).filter(plan => plan.id !== planId);

        let updatedProducts = prev.products || [];
        if (deletedPlan && Array.isArray(deletedPlan.products)) {
          updatedProducts = updatedProducts.map(prod => {
            if (deletedPlan.products.some(p => p.productId === prod.productId)) {
              return { ...prod };
            }
            return prod;
          });
        }

        return {
          ...prev,
          exitPlans: updatedExitPlans,
          products: updatedProducts
        };
      });
    } catch (error) {
      dispatch(setSnackbar({
        open: true,
        message: ERROR_MESSAGES.DELETE_FAILED,
        error: true
      }));
      throw error;
    } finally {
      setLoading(false);
    }
  };

  /**
   * Submits an exit plan
   * @param {number} exitPlanId - ID of the plan to submit
   * @returns {Promise<void>}
   */
  const submitExitPlan = async (exitPlanId) => {
    setSubmitLoading(true);
    try {
      await axiosInstance.post(`${API_ENDPOINTS.SUBMIT_EXIT_PLAN}${exitPlanId}/submit`);
      // Update status to SUBMITTED in Redux state
      dispatch((dispatch, getState) => {
        const current = getState().requestData.requestData;
        const updatedExitPlans = (current.exitPlans || []).map(plan =>
          plan.id === exitPlanId
            ? { ...plan, status: isExemption(plan) ? 'PENDING_LOCAL_RISK_ASSESSMENT' : 'SUBMITTED' }
            : plan
        );
        dispatch(setData({
          ...current,
          exitPlans: updatedExitPlans
        }));
      });
      // Update status to SUBMITTED in local state
      setRequestData(prev => {
        if (!prev) return prev;
        const updatedExitPlans = (prev.exitPlans || []).map(plan =>
          plan.id === exitPlanId
            ? { ...plan, status: isExemption(plan) ? 'PENDING_LOCAL_RISK_ASSESSMENT' : 'SUBMITTED' }
            : plan
        );
        return {
          ...prev,
          exitPlans: updatedExitPlans
        };
      });
      // Reload and preserve plan/tab selection
      window.location.replace(
        `${window.location.pathname}?showSnackbar=planSubmitted&selectedPlanId=${exitPlanId}&openPlanStatusTab=1`
      );
    } catch (error) {
      dispatch(setSnackbar({
        open: true,
        message: "Failed to submit.",
        error: true
      }));
      throw error;
    } finally {
      setSubmitLoading(false);
    }
  };

  // ===== Approving Manager Check =====
  const isApprovingManager = !!(
    requestData &&
    requestData.exitRequest &&
    Array.isArray(requestData.exitRequest.approvingManagers) &&
    requestData.exitRequest.approvingManagers.some(manager => manager.id === userId)
  );

  // ===== Redux Update Helpers =====
  /**
   * Updates exit plans in Redux state
   * @param {Object} planData - New or updated plan data
   * @param {boolean} updating - Whether updating existing plan
   */
  const updateReduxExitPlans = (planData, updating) => {
    dispatch((dispatch, getState) => {
      const current = getState().requestData.requestData;
      let updatedExitPlans;

      if (updating) {
        // Update existing plan
        updatedExitPlans = (current.exitPlans || []).map(plan =>
            plan.id === planData.id ? planData : plan
        );
      } else {
        // Add new plan
        updatedExitPlans = [...(current.exitPlans || []), planData];
      }

      // Update Redux state
      dispatch(setData({
        ...current,
        exitPlans: updatedExitPlans
      }));
    });
  };

  /**
   * Removes a plan from Redux exit plans
   * @param {number} planId - ID of the plan to remove
   */
  const removeFromReduxExitPlans = (planId) => {
    dispatch((dispatch, getState) => {
      const current = getState().requestData.requestData;
      const updatedExitPlans = (current.exitPlans || []).filter(plan => plan.id !== planId);

      dispatch(setData({
        ...current,
        exitPlans: updatedExitPlans
      }));
    });
  };

  // ===== Return Hook Data =====
  return {
    // Loading state
    loading,

    // Request data
    requestData,

    // Form validation
    formErrors,
    validateForm,

    // Form field values
    exitDate,
    endOfProductionDate,
    endOfImportationDate,
    replacementPlan,
    liquidationPlan,
    additionalComments,

    // Plan type + exemption fields
    planType,
    setPlanType,
    toImpactEuros,
    exceptionStartDate,
    exceptionEndDate,
    justification,
    safetyAssessmentReference,
    productTypes,
    brands,
    setToImpactEuros,
    setExceptionStartDate,
    setExceptionEndDate,
    setJustification,
    setSafetyAssessmentReference,
    setProductTypes,
    setBrands,

    // Support team values
    needsSupport,
    marketingSupport,
    supplyChainSupport,
    selectedMarketingMember,
    selectedSupplyChainMember,

    // Form field setters
    setExitDate,
    setEndOfProductionDate,
    setEndOfImportationDate,
    setReplacementPlan,
    setLiquidationPlan,
    setAdditionalComments,

    // Support team setters
    setNeedsSupport,
    setMarketingSupport,
    setSupplyChainSupport,
    setSelectedMarketingMember,
    setSelectedSupplyChainMember,

    // Form actions
    setFormErrors,
    handleSubmit,

    // API actions
    createExitPlan,
    deleteExitPlan,
    submitExitPlan,
    submitLoading,

    // Read-only state
    readOnly,
    isApprovingManager,

    // User permissions
    canEdit,
  };
};